<script setup lang="ts">
import { RBtn } from '@therobotcompany/robco-frontend-library';
import { computed, ref, watch } from 'vue';
import { loadGroupChildren } from '@/api/GroupApi';
import type { Group } from '@/models/Group';
import type { GroupChildren } from '@/models/GroupChildren';
import RobotCard from '@/components/fleet/RobotCard.vue';
import CreateGroupDialog from '@/components/fleet/CreateGroupDialog.vue';
import DeleteGroupDialog from '@/components/fleet/DeleteGroupDialog.vue';
import EditGroupDialog from '@/components/fleet/EditGroupDialog.vue';
import ManageGroupDialog from '@/components/fleet/ManageGroupDialog.vue';
import RegisterRobotDialog from '@/components/fleet/RegisterRobotDialog.vue';
import { Role } from '@/models/Roles';

const props = defineProps<{ group: Group }>();
const emit = defineEmits(['reload-root']);

const loading = ref(false);
const groupChildren = ref<GroupChildren | null>(null);
const createDialog = ref(false);
const deleteDialog = ref(false);
const editDialog = ref(false);
const managementDialog = ref(false);
const robotRegistrationDialog = ref(false);

const reloadChildren = async () => {
  loading.value = true;
  try {
    groupChildren.value = await loadGroupChildren(props.group.uuid);
  } catch (error) {
    console.error('Failed to fetch group children:', error);
  } finally {
    loading.value = false;
  }
};

const fetchChildren = async () => {
  if (loading.value || groupChildren.value) {
    return;
  }
  await reloadChildren();
};

const openCreateDialog = () => {
  createDialog.value = true;
};

const openEditDialog = () => {
  editDialog.value = true;
};

const openDeleteDialog = () => {
  deleteDialog.value = true;
};

const openManagementDialog = () => {
  managementDialog.value = true;
};

const openRobotRegistrationDialog = () => {
  robotRegistrationDialog.value = true;
};

const reloadRoot = () => {
  emit('reload-root');
};

const isUserGroupAdmin = computed(
  () => !!(groupChildren.value && groupChildren.value.role === Role.ADMIN),
);

watch(() => props.group.uuid, fetchChildren, { immediate: true });
</script>

<template>
  <v-expansion-panel>
    <v-expansion-panel-title style="height: 48px" @click="fetchChildren">
      <span class="m-2 font-weight-bold">{{ props.group.name }}</span>
      <v-menu v-if="isUserGroupAdmin" location="end">
        <template v-slot:activator="{ props }">
          <v-btn
            class="mx-2"
            variant="text"
            size="small"
            icon="mdi-dots-vertical"
            v-bind="props"
            @click.stop
          >
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="openEditDialog">
            <v-list-item-title>{{ $gettext('Edit') }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="openManagementDialog">
            <v-list-item-title>{{ $gettext('Manage') }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="openDeleteDialog">
            <v-list-item-title class="text-error">{{
              $gettext('Delete')
            }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
        size="24"
        class="ml-2"
      ></v-progress-circular>
    </v-expansion-panel-title>
    <v-expansion-panel-text>
      <div
        v-if="!loading && groupChildren && groupChildren.role == Role.ADMIN"
        class="my-2"
      >
        <r-btn
          class="mr-2 mb-2"
          variant="outlined"
          icon="mdi-plus"
          @click="openCreateDialog"
        >
          {{ $gettext('Add Sub-Group') }}
        </r-btn>
        <r-btn
          variant="outlined"
          icon="mdi-plus"
          @click="openRobotRegistrationDialog"
        >
          {{ $gettext('Add Robot') }}
        </r-btn>
      </div>
      <div v-if="loading" class="empty-group-message">
        <span>{{ $gettext('Loading') }}...</span>
      </div>
      <div
        v-else-if="
          !groupChildren?.groups.length && !groupChildren?.controlUnits.length
        "
        class="empty-group-message"
      >
        <span>{{ $gettext('Empty Group') }}</span>
      </div>
      <div v-else class="mt-6">
        <v-expansion-panels v-if="groupChildren?.groups.length" multiple>
          <template
            v-for="subGroup in groupChildren.groups"
            :key="subGroup.uuid"
          >
            <GroupPanel :group="subGroup" @reload-root="reloadRoot" />
          </template>
        </v-expansion-panels>
        <v-row class="mt-4">
          <v-col
            v-for="robot in groupChildren?.controlUnits"
            :key="robot.serialNumber"
            cols="12"
            md="6"
            lg="4"
            xl="3"
            xxl="2"
          >
            <RobotCard
              :robot="robot"
              :moveEnabled="isUserGroupAdmin"
              @robot-moved="reloadRoot"
            />
          </v-col>
        </v-row>
      </div>
    </v-expansion-panel-text>
  </v-expansion-panel>
  <CreateGroupDialog
    v-model:isOpen="createDialog"
    :parentUuid="group.uuid"
    @group-created="reloadChildren"
  />
  <DeleteGroupDialog
    v-model:isOpen="deleteDialog"
    :group="group"
    @group-deleted="reloadRoot"
  />
  <EditGroupDialog v-model:isOpen="editDialog" :group="group" />
  <ManageGroupDialog
    v-model:isOpen="managementDialog"
    :group="group"
    @current-user-update="reloadChildren"
    @current-user-delete="reloadRoot"
  />
  <RegisterRobotDialog
    v-model:isOpen="robotRegistrationDialog"
    :groupUuid="group.uuid"
    @robot-registered="reloadChildren"
  />
</template>

<style scoped>
.empty-group-message {
  text-align: center;
  font-style: italic;
  color: var(--v-theme-on-background);
}
</style>
